/**
 * Field Metadata Service
 * 
 * Provides field metadata extraction and formatting for dynamic field rendering.
 * Generates user-friendly labels, help text, and extracts validation rules
 * from schema definitions.
 * 
 * Evidence: Backend schema provides field types, validation rules, options for each field
 * Located in ClaimController.js baseFields and typeSpecificFields definitions
 */

import { conditionalFieldService } from './conditionalFieldService';

export const fieldMetadataService = {
  /**
   * Get comprehensive field metadata including validation rules
   * @param {string} fieldName - Name of the field
   * @param {string} claimType - Type of claim
   * @param {Object} schema - Schema object
   * @returns {Object|null} - Field metadata or null if field not found
   */
  getFieldMetadata(fieldName, claimType, schema) {
    const fieldDef = conditionalFieldService.getFieldDefinition(fieldName, claimType, schema);
    
    if (!fieldDef) return null;
    
    return {
      type: fieldDef.type || 'text',
      required: fieldDef.required || false,
      options: fieldDef.options || [],
      validation: fieldDef.validation || {},
      dependsOn: fieldDef.dependsOn || null,
      label: this.generateFieldLabel(fieldName),
      helpText: this.getFieldHelpText(fieldName, fieldDef)
    };
  },

  /**
   * Generate user-friendly field labels
   * @param {string} fieldName - Name of the field
   * @returns {string} - User-friendly label
   */
  generateFieldLabel(fieldName) {
    const labelMappings = {
      'monthlyPaymentAmount': 'Monthly Payment Amount',
      'paymentFrequency': 'Payment Frequency',
      'disabilityType': 'Disability Type',
      'dateOfInjury': 'Date of Injury',
      'paymentStartDate': 'Payment Start Date',
      'paymentEndDate': 'Payment End Date',
      'estimatedPaymentDuration': 'Estimated Payment Duration',
      'policyNumber': 'Policy Number',
      'claimantName': 'Claimant Name',
      'claimantEmail': 'Email Address',
      'claimantMobile': 'Mobile Number',
      'accountNumber': 'Account Number',
      'deceasedName': 'Deceased Name',
      'deceasedDateOfDeath': 'Date of Death',
      'causeOfDeath': 'Cause of Death',
      'causeOfDisability': 'Cause of Disability',
      'disabilityCause': 'Cause of Disability',
      'illnessCause': 'Cause of Illness',
      'relationshipToDeceased': 'Relationship to Deceased',
      'diagnosis': 'Diagnosis',
      'diagnosisDate': 'Diagnosis Date',
      'eventDate': 'Event Date',
      'illnessStage': 'Illness Stage',
      'parentRelationshipVerified': 'Parent Relationship Verified'
    };
    
    return labelMappings[fieldName] || 
           fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  },

  /**
   * Get context-aware help text for fields
   * @param {string} fieldName - Name of the field
   * @param {Object} fieldDef - Field definition from schema
   * @returns {string} - Help text for the field
   */
  getFieldHelpText(fieldName, fieldDef) {
    const baseHelpTexts = {
      'monthlyPaymentAmount': 'Enter the monthly payment amount for TTD claims',
      'paymentFrequency': 'Select how often payments will be made',
      'paymentStartDate': 'Date when TTD payments should begin',
      'paymentEndDate': 'Expected end date for TTD payments',
      'estimatedPaymentDuration': 'Estimated duration of TTD payments in months',
      'policyNumber': 'Enter your policy number (8-20 digits)',
      'claimantEmail': 'Must be a valid email address',
      'claimantMobile': 'Include country code (e.g. +233242XXXXXXX)',
      'accountNumber': '8–20 digits, numbers only',
      'deceasedDateOfDeath': 'Format: YYYY-MM-DD',
      'dateOfInjury': 'Format: YYYY-MM-DD',
      'diagnosisDate': 'Format: YYYY-MM-DD',
      'eventDate': 'Date when the incident or event occurred',
      'causeOfDeath': 'Select the primary cause of death from the available options',
      'causeOfDisability': 'Select the primary cause of disability from the available options',
      'disabilityCause': 'Select the primary cause of disability from the available options',
      'illnessCause': 'Select the primary cause of illness from the available options',
      'diagnosis': 'Select the diagnosis or cause from the available options'
    };
    
    return baseHelpTexts[fieldName] || '';
  }
};